import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useInitialSuggestionsPolling from 'hooks/useInitialSuggestionsPolling';
import usePrevious from 'hooks/usePrevious';
import useVideoZipRequestPolling from 'hooks/useVideoZipRequestPolling';
import {
  getEpisodeById,
  getSubscriptionById,
} from 'redux/middleware/api/podcast-service/actions';
import { getAudioUpload } from 'redux/middleware/api/recording-upload-service/actions';
import { getUserIntegrators, loggedInSelector } from 'redux/modules/auth';
import {
  groupClipSuggestionsIsLoadingSelector,
  loadGroupClipSuggestions,
  loadSubscriptionItemInformation,
  pollForGroupClipSuggestion,
} from 'redux/modules/clip-select';
import { getMyDisplayPref } from 'redux/modules/display-pref';
import { pollGroupClipSuggestionsIdsSelector } from 'redux/modules/entities';
import { showError } from 'redux/modules/notification/actions';
import { goToAppHome } from 'redux/modules/router/actions';
import {
  autoCreateSubscriptionIdSelector,
  hasSubscriptionItemInformationSelector,
} from './selectors';
import useAutomationSuggestedClipInfo from './useAutomationSuggestedClipInfo';

export default function useAutomationSuggestedClipsPage() {
  const {
    episodeId,
    episode,
    podcast,
    subscriptionItemId,
    groupRequestId,
    entireAudioInstanceId,
  } = useAutomationSuggestedClipInfo();

  const location = useLocation();

  const isLoadingStatus = useSelector(groupClipSuggestionsIsLoadingSelector);

  const pollGroupClipSuggestionsIds = useSelector(
    pollGroupClipSuggestionsIdsSelector,
  );

  const autoCreateSubId = useSelector(state =>
    autoCreateSubscriptionIdSelector(state, subscriptionItemId),
  );

  const hasSubscriptionItemInformation = useSelector(state =>
    hasSubscriptionItemInformationSelector(state, subscriptionItemId),
  );

  const isLoggedIn = useSelector(loggedInSelector);

  const prevEntireAudioInstanceId = usePrevious(entireAudioInstanceId);

  const dispatch = useDispatch();

  useInitialSuggestionsPolling({
    suggestionIds: pollGroupClipSuggestionsIds,
    isDisabled: isLoadingStatus,
    pollSuggestion: (autoCreateEpisodeId: number) =>
      dispatch(pollForGroupClipSuggestion(autoCreateEpisodeId)),
  });

  useVideoZipRequestPolling({
    groupRequestId:
      episodeId && subscriptionItemId && groupRequestId
        ? groupRequestId
        : undefined,
  });

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        dispatch(getMyDisplayPref());
      })
      .then(() => {
        if (isLoggedIn) {
          return dispatch(getUserIntegrators());
        }

        return null;
      })
      .then(() => dispatch(loadSubscriptionItemInformation(subscriptionItemId)))
      .catch(() => {
        dispatch(
          showError({
            message: 'Error loading clips',
            code: 'ER014',
            dismissAfterSec: 5,
          }),
        );

        dispatch(goToAppHome());
      });
  }, [dispatch, isLoggedIn, subscriptionItemId]);

  useEffect(() => {
    if (hasSubscriptionItemInformation && autoCreateSubId) {
      dispatch(getSubscriptionById(autoCreateSubId));
      dispatch(getEpisodeById(Number(episodeId)));
    }
  }, [autoCreateSubId, dispatch, episodeId, hasSubscriptionItemInformation]);

  useEffect(() => {
    if (!prevEntireAudioInstanceId && entireAudioInstanceId) {
      dispatch(getAudioUpload(entireAudioInstanceId));
    }
  }, [dispatch, entireAudioInstanceId, prevEntireAudioInstanceId]);

  useEffect(() => {
    if (!episodeId || !subscriptionItemId) {
      return;
    }

    dispatch(
      loadGroupClipSuggestions(
        Number(episodeId),
        subscriptionItemId,
        groupRequestId,
      ),
    );
  }, [dispatch, episodeId, groupRequestId, location, subscriptionItemId]);

  return {
    isLoading:
      !hasSubscriptionItemInformation ||
      !episode ||
      !podcast ||
      isLoadingStatus,
  };
}
